import {TimelineContextMenu} from "../timelineContextMenu";
import {CombinedTimelineDayComponentConfig, WorkingTimeTimelineDayContent} from "../timelineTypes";
import {hasWorkingTimeTypesToAdd} from "../timelineHelper";
import {htmlEscape} from "@/shared/utils/string";
import {parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {WorkingTime} from "@/shared/timr";

/**
 * Component to display validation messages in a timeline day.
 * This component is used inside <code>TimelineDay</code> to render validation messages for a specific day. If available this component is appended to the day container.
 * For an overview of the timeline architecture, have a look into the UML diagram "timeline_web_components.png"
 */
export class CombinedTimelineDayValidations extends HTMLElement {
	private _timelineDay: WorkingTimeTimelineDayContent;
	private _config: CombinedTimelineDayComponentConfig;
	private _contextMenuForDate: (date: Temporal.PlainDate | null) => TimelineContextMenu<WorkingTime>;

	set timelineDay(value: WorkingTimeTimelineDayContent) {
		this._timelineDay = value;
	}

	set config(value: CombinedTimelineDayComponentConfig) {
		this._config = value;
	}

	set contextMenuForDate(value: (date: Temporal.PlainDate | null) => TimelineContextMenu<WorkingTime>) {
		this._contextMenuForDate = value;
	}

	/**
	 * Main entry point to render the validation messages.
	 */
	public connectedCallback(): void {
		// timeline day, config and context menu are required to render validations
		if (!this._timelineDay || !this._config || !this._contextMenuForDate) {
			return;
		}

		// Only unreferenced validation results are rendered in this component.
		const validations = this._timelineDay.validationResults.filter(v => !v.recordedEntry && !v.recordedEntryRef);
		if (validations.length === 0) {
			return;
		}

		const validationRows = validations.map(validation => {
			const tr = document.createElement("tr");
			tr.innerHTML = `
						<td class="pb-5"><i class="fa fa-validation-severity-${validation.severity} position-left" title="${messages["recordingValidation.severity." + validation.severity]}"></i></td>
						<td class="pb-5">${htmlEscape(messages.withArguments(validation.messageCode, ...validation.messageArguments))}</td>
						`;
			return tr;
		});

		const containerEl = document.createElement("div");
		containerEl.append(...validationRows);

		this.append(containerEl);

		if (this.shouldShowAddWorkingTimePanel()) {
			const panelEl = document.createElement("div");
			panelEl.classList.add("add-working-time-panel");
			panelEl.innerHTML = `
						<div class="dropdown" data-context-menu-container>
							<div class="btn btn-link btn-link-primary btn-xs no-padding-left" data-context-menu-button style="margin: auto auto auto 0;">
								<i class="fal fa-plus position-left"></i> ${messages["timeline.workingTime.add"]}
							</div>
						</div>`;

			this.append(panelEl);

			this._contextMenuForDate(parseISODate(this._timelineDay.date)).bindToElement(panelEl);
		}
	}

	private shouldShowAddWorkingTimePanel(): boolean {
		return this._timelineDay.items.length === 0 &&
			this._timelineDay.validationResults.find(v => v.messageCode === "validationResult.missingEntryOnWorkingDay") &&
			hasWorkingTimeTypesToAdd(this._config);
	}
}
